import React from 'react';
import {
  ClipboardDocumentListIcon,
  DocumentArrowUpIcon,
  DocumentChartBarIcon,
  BellAlertIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import { TodayAttentionItem, TodayFocusModel, TODAY_SECONDARY_LIMIT } from './dashboardUtils';
import { t } from '../../utils/translations';
import Button, { ButtonIcon, ButtonVariant } from '../ui/Button';
import { actionIcon } from '../ui/actionIcons';

interface TodayFocusSectionProps {
  focus: TodayFocusModel;
  loading?: boolean;
  onPrepare: (item: TodayAttentionItem) => void;
  onReport: (item: TodayAttentionItem) => void;
  onSchedule: (item: TodayAttentionItem) => void;
  onConsent: (item: TodayAttentionItem) => void;
  onClarify: (item: TodayAttentionItem) => void;
  onAddNote: (item: TodayAttentionItem) => void;
  onMonthlyReport: () => void;
}

function secondaryAccentClass(kind: TodayAttentionItem['kind']): string {
  if (kind === 'report') return 'border border-red-100 border-l-red-500 bg-red-50';
  if (kind === 'monthly_report') return 'border border-brand-100 border-l-brand-600 bg-brand-50';
  if (kind === 'frequency') return 'border border-brand-100 border-l-brand-600 bg-brand-50';
  if (kind === 'consent' || kind === 'clarify') {
    return 'border border-slate-200 border-l-slate-400 bg-white';
  }
  return 'border border-brand-100 border-l-brand-500 bg-brand-50';
}

function ctaVariant(kind: TodayAttentionItem['kind']): ButtonVariant {
  if (kind === 'report') return 'danger';
  if (kind === 'frequency' || kind === 'prepare' || kind === 'monthly_report') return 'primary';
  return 'secondary';
}

/** Report uses the "+ Raport" label instead of an icon, same as the rest of the app. */
function ctaIcon(kind: TodayAttentionItem['kind']): ButtonIcon | undefined {
  if (kind === 'report') return undefined;
  if (kind === 'monthly_report') return actionIcon.monthlyReport;
  if (kind === 'prepare') return actionIcon.view;
  if (kind === 'consent') return actionIcon.upload;
  if (kind === 'clarify') return BookOpenIcon;
  return actionIcon.schedule;
}

/** Priority bang for „Mai cere atenție” — not action-specific icons. */
function PriorityBang({ kind }: { kind: TodayAttentionItem['kind'] }) {
  const bangColor =
    kind === 'report'
      ? 'text-red-600'
      : kind === 'consent' || kind === 'clarify'
        ? 'text-slate-400'
        : 'text-brand-600';
  return (
    <ExclamationTriangleIcon
      className={`h-5 w-5 shrink-0 ${bangColor}`}
      aria-hidden
    />
  );
}

function PrimaryActionIcon({ kind }: { kind: TodayAttentionItem['kind'] }) {
  const wrap =
    'flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-50';
  const icon = 'h-5 w-5 text-brand-600';
  if (kind === 'report') {
    return (
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-50">
        <ClipboardDocumentListIcon className="h-5 w-5 text-red-600" aria-hidden />
      </div>
    );
  }
  if (kind === 'monthly_report') {
    return (
      <div className={wrap}>
        <DocumentChartBarIcon className={icon} aria-hidden />
      </div>
    );
  }
  if (kind === 'frequency') {
    return (
      <div className={wrap}>
        <BellAlertIcon className={icon} aria-hidden />
      </div>
    );
  }
  if (kind === 'consent') {
    return (
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-100">
        <DocumentArrowUpIcon className="h-5 w-5 text-slate-600" aria-hidden />
      </div>
    );
  }
  return (
    <div className={wrap}>
      <CalendarIcon className={icon} aria-hidden />
    </div>
  );
}

function runAction(
  item: TodayAttentionItem,
  handlers: Pick<
    TodayFocusSectionProps,
    'onPrepare' | 'onReport' | 'onSchedule' | 'onConsent' | 'onClarify' | 'onMonthlyReport'
  >
) {
  if (item.kind === 'prepare') handlers.onPrepare(item);
  else if (item.kind === 'report') handlers.onReport(item);
  else if (item.kind === 'frequency') handlers.onSchedule(item);
  else if (item.kind === 'monthly_report') handlers.onMonthlyReport();
  else if (item.kind === 'clarify') handlers.onClarify(item);
  else handlers.onConsent(item);
}

function ctaLabel(item: TodayAttentionItem): string {
  if (item.kind === 'prepare') return t.dashboard.todayFocus.prepareCta;
  if (item.kind === 'report') return t.dashboard.todayFocus.reportCta;
  if (item.kind === 'frequency') return t.dashboard.todayFocus.scheduleCta;
  if (item.kind === 'monthly_report') return t.dashboard.todayFocus.monthlyReportCta;
  if (item.kind === 'clarify') return t.dashboard.todayFocus.openTablou;
  return t.dashboard.todayFocus.consentCta;
}

/** Bleeds to the card edges; the gradient blends its warm background into the white card. */
function FocusIllustration() {
  return (
    <div className="pointer-events-none relative hidden min-h-[150px] w-[38%] max-w-[300px] shrink-0 self-stretch sm:block">
      <img
        src={`${process.env.PUBLIC_URL}/dashboard-focus-scene.jpg`}
        alt=""
        className="absolute inset-0 h-full w-full select-none object-cover object-[60%_70%]"
        draggable={false}
      />
      <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent" />
    </div>
  );
}

/** On mobile there is no room for a side panel, so the scene sits behind the text under a white veil. */
function FocusBackdrop() {
  return (
    <div className="pointer-events-none absolute inset-0 sm:hidden">
      <img
        src={`${process.env.PUBLIC_URL}/dashboard-focus-scene.jpg`}
        alt=""
        className="h-full w-full select-none object-cover object-[78%_72%]"
        draggable={false}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-white from-40% via-white/85 via-65% to-white/35" />
    </div>
  );
}

export function TodaySecondaryList({
  items,
  onPrepare,
  onReport,
  onSchedule,
  onConsent,
  onClarify,
  onMonthlyReport,
}: {
  items: TodayAttentionItem[];
  onPrepare: (item: TodayAttentionItem) => void;
  onReport: (item: TodayAttentionItem) => void;
  onSchedule: (item: TodayAttentionItem) => void;
  onConsent: (item: TodayAttentionItem) => void;
  onClarify: (item: TodayAttentionItem) => void;
  onMonthlyReport: () => void;
}) {
  if (items.length === 0) return null;
  const handlers = { onPrepare, onReport, onSchedule, onConsent, onClarify, onMonthlyReport };

  return (
    <div>
      <h2 className="mb-3 text-sm font-semibold text-slate-900">
        {t.dashboard.todayFocus.alsoNeedsAttention}
      </h2>
      <ul className="space-y-2">
        {items.slice(0, TODAY_SECONDARY_LIMIT).map((item) => (
          <li
            key={`${item.kind}-${item.caseId}`}
            className={`flex flex-wrap items-center gap-3 rounded-xl border-l-4 px-3 py-2.5 ${secondaryAccentClass(
              item.kind
            )}`}
          >
            <PriorityBang kind={item.kind} />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-slate-900">
                <span className="font-medium">{item.title}</span>
                <span className="text-slate-600"> · {item.meta}</span>
              </p>
            </div>
            <Button
              size="sm"
              variant={ctaVariant(item.kind)}
              icon={ctaIcon(item.kind)}
              className="shrink-0"
              onClick={() => runAction(item, handlers)}
            >
              {ctaLabel(item)}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Left column: priority action card (any kind) + secondary alerts. */
const TodayFocusSection: React.FC<TodayFocusSectionProps> = ({
  focus,
  loading,
  onPrepare,
  onReport,
  onSchedule,
  onConsent,
  onClarify,
  onAddNote,
  onMonthlyReport,
}) => {
  const handlers = { onPrepare, onReport, onSchedule, onConsent, onClarify, onMonthlyReport };
  const showAddNote =
    Boolean(focus.primary?.caseItem) && focus.primary?.kind !== 'monthly_report';

  if (loading) {
    return (
      <section className="space-y-5">
        <div className="h-44 animate-pulse rounded-xl bg-slate-100" />
        <div className="h-20 animate-pulse rounded-xl bg-slate-100" />
      </section>
    );
  }

  return (
    <section className="flex h-full flex-col space-y-5">
      {focus.primary ? (
        <div className="relative overflow-hidden rounded-xl border border-slate-200 border-l-[5px] border-l-brand-600 bg-white shadow-sm">
          <FocusBackdrop />
          <div className="relative flex items-stretch">
            <div className="min-w-0 flex-1 p-5 sm:p-6 lg:p-7">
              <p className="text-sm font-medium text-slate-500">
                {t.dashboard.todayFocus.nextAction}
              </p>
              <div className="mt-2 flex items-start gap-3">
                <PrimaryActionIcon kind={focus.primary.kind} />
                <div className="min-w-0 flex-1">
                  <p className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
                    {focus.primary.title}
                  </p>
                  <p className="mt-1.5 text-sm text-slate-600">{focus.primary.meta}</p>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2.5">
                <Button
                  variant="primary"
                  icon={ctaIcon(focus.primary.kind)}
                  onClick={() => runAction(focus.primary!, handlers)}
                >
                  {ctaLabel(focus.primary)}
                </Button>
                {showAddNote && (
                  <Button onClick={() => onAddNote(focus.primary!)}>
                    {t.cases.cockpit.addNoteCta}
                  </Button>
                )}
              </div>
            </div>
            <FocusIllustration />
          </div>
        </div>
      ) : (
        <div className="relative overflow-hidden rounded-xl border border-slate-200 border-l-[5px] border-l-slate-300 bg-white shadow-sm">
          <FocusBackdrop />
          <div className="relative flex items-stretch">
            <div className="min-w-0 flex-1 p-5 sm:p-6 lg:p-7">
              <p className="text-base font-semibold text-slate-900 sm:text-lg">
                {t.dashboard.todayFocus.allClearTitle}
              </p>
              <p className="mt-1.5 text-sm text-slate-500">
                {t.dashboard.todayFocus.allClearBody}
              </p>
            </div>
            <FocusIllustration />
          </div>
        </div>
      )}

      <TodaySecondaryList
        items={focus.secondary}
        onPrepare={onPrepare}
        onReport={onReport}
        onSchedule={onSchedule}
        onConsent={onConsent}
        onClarify={onClarify}
        onMonthlyReport={onMonthlyReport}
      />
    </section>
  );
};

export default TodayFocusSection;
